<template>
  <form
    class="writeoff-form"
    v-on:submit.prevent="onSubmit"
  >
    <div class="task-creation__data">
      <div class="task-creation__data__label">Дата списания:</div>
      <date-picker-dialog
        v-bind:allowed-dates="allowedDates"
        v-bind:value="currentDate"
        v-on:input="setNewDate"
      >
        <template v-slot:trigger="{ listenerObject }">
          <button
            title="Сменить дату"
            v-if="currentDate"
            type="button"
            v-on="listenerObject"
          >
            <input-text
              v-bind:readonly="true"
              v-bind:value="formattedDate"
            />
          </button>
        </template>
      </date-picker-dialog>
    </div>
    <fieldset
      class="writeoff-form__service"
      v-for="{
        name,
        id,
        unit,
        count,
        index,
        price,
        getFinalPrice,
        countInWarehouse,
      } in products"
      v-bind:key="index"
    >
      <div class="writeoff-form__service-title">Товар №{{ index + 1 }}</div>
      <input v-bind:name="`products.${index}.id`" type="hidden" v-bind:value="id">
      <input-text
        v-bind:disabled="true"
        title="Название товара"
        v-bind:value="name"
      />
      <input-text
        v-bind:disabled="true"
        title="Единицы измерения"
        v-bind:value="unit"
      />
      <input-text
        v-bind:disabled="true"
        title="Количество на складе"
        v-bind:value="countInWarehouse"
      />
      <span v-if="countInWarehouse == 0">
        Товар закончился. Списание недоступно
      </span>
      <input-number
        v-else
        v-bind:max="countInWarehouse"
        min="1"
        title="Введите количество"
        allowed-null
        without-native-controls
        v-bind:name="`products.${index}.count`"
        v-bind:value="count"
        v-bind:violations="productsInputErrors[index].count"
        v-on:blur="onBlurCountInput(index, count)"
        v-on:focus="onFocusCountInput(index, count)"
        v-on:input="onChangeCount(index, $event)" />
      <div class="writeoff-form__prices">
        <input-layout class="writeoff-form__product-price" title="Цена">
          <price-input
            v-bind:value="price"
            v-on:input="onChangePrice(index, $event)"
          />
        </input-layout>
        <input-layout title="Итого">
          <the-rub
            currency="руб"
            v-bind:kop="getFinalPrice()"
          />
        </input-layout>
      </div>
      <button
        class="writeoff-form__service-remove"
        type="button"
        v-on:click="removeProduct(index)"
      >Удалить</button>
    </fieldset>
    <autocomplete-input
      :key="`input-product-${autocompleteKey}`"
      item-text="name"
      item-value="id"
      label="Товар"
      v-bind:menu-props="{ maxWidth: 'min(80%, 586px)' }"
      v-bind:items="productOptions"
      v-bind:clearable="true"
      v-bind:violations="productInputErrors"
      v-model="selectedProduct"
      v-on:input="selectingProduct"
      attach />
    <button
      class="writeoff-form__submit"
      type="submit"
    >Сохранить</button>
  </form>
</template>
<script>
import { List } from 'monet';
import formMixin from '@/mixins/formMixin';
import InputLayout from '@/components/InputLayout.vue';
import InputNumber from '@/components/InputNumber.vue';
import InputText from '@/components/InputText.vue';
import TheRub from '@/components/TheRub.vue';
import addingServicesFromPriceMixin from '@/mixins/addingServicesFromPriceMixin';
import AutocompleteInput from '@/components/AutocompleteInput.vue';
import PriceInput from '@/components/PriceInput.vue';
import { Product, WriteOffRequest } from '@/storage/gateways/WriteOffGateway';
import DatePickerDialog from '@/components/DatePickerDialog.vue';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Форма списания товара на склад клиники.
 * @type {Vue}
 */
export default {
  components: {
    PriceInput,
    AutocompleteInput,
    InputLayout,
    InputNumber,
    InputText,
    TheRub,
    DatePickerDialog,
  },
  computed: {
    productInputErrors: resolveInputErrors('product'),
    productsInputErrors() {
      const countProducts = this.products.length - 1;
      const errors = new Array(this.products.length).fill(null);
      for (let i = 0; i <= countProducts; i += 1) {
        const count = this.inputErrors[`product-${i}-count`] ?? [];
        const price = this.inputErrors[`product-${i}-price`] ?? [];
        errors[i] = {
          count,
          price,
        };
      }
      return errors;
    },
    formattedDate() {
      let month = this.currentDate.getMonth() + 1;
      month = month > 9 ? month : `0${month}`;
      return `${this.currentDate.getDate()}.${month}.${this.currentDate.getFullYear()}`;
    },
  },
  data() {
    return {
      /**
       * @description Товары списания.
       * @type {Array<Product>}
       */
      products: this.initialProducts,
      autocompleteKey: 0,
      selectedProduct: '',
      /**
       * @description дата списания.
       * @type {String}
       */
      currentDate: this.initialDate,
    };
  },
  methods: {
    /**
     * @description Установить новую дату
     * @param {Date} date
     */
    setNewDate(date) {
      this.currentDate = date;
    },
    /**
     * @description Функция описывающая условие доступности выбора дат в datepicker
     * @param {String} candidate Строка с датой в формате Y-m-d
     * @return {boolean}
     */
    allowedDates(candidate) {
      return (new Date(candidate)) <= new Date();
    },
    /**
     * @description Обработать выбор товара.
     * @returns {void}
     */
    selectingProduct(productId) {
      const product = this.productOptions.find((item) => item.id === productId);
      this.selectedProduct = '';
      this.autocompleteKey += 1;
      this.onAddingPriceProduct(product);
      this.$emit('change');
    },
    /**
     * @description Обработать добавление товара.
     * @param {Object} product Товар.
     * @returns {void}
     */
    onAddingPriceProduct(product) {
      const addingProduct = new Product(
        this.products.length,
        product.id,
        product.name,
        0,
        product.unit,
        product.price,
        product.countInWarehouse,
      );
      this.$set(this.products, this.products.length, addingProduct);
    },
    /**
     * @description Обработать изменение количества товара.
     * @param {number} productIndex Индекс товара.
     * @param {number} count        Количество.
     * @returns {void}
     */
    onChangeCount(productIndex, count) {
      const updater = (product) => product.setCount(count);
      this.products = this.updateProductByIndex(updater)(productIndex);
    },
    /**
     * @description Обработать изменения единицы измерения.
     * @param {number} productIndex Индекс товара.
     * @param {string} price        Цена товара.
     * @returns {void}
     */
    onChangePrice(productIndex, price) {
      const updater = (product) => product.setPrice(price);
      this.products = this.updateProductByIndex(updater)(productIndex);
    },
    /**
     * @description Обработать потерю фокуса с поля количества товара.
     * @param {number} serviceIndex Индекс товара.
     * @param {number} count        Количество.
     * @returns {void}
     */
    onBlurCountInput(serviceIndex, count) {
      if (count === '') {
        this.onChangeCount(serviceIndex, '1');
      }
    },
    /**
     * @description Обработать фокус на поле количества товара.
     * @param {number} serviceIndex Индекс товара.
     * @param {number} count        Количество.
     * @returns {void}
     */
    onFocusCountInput(serviceIndex, count) {
      if (count.toString().charAt(0) === '0') {
        this.onChangeCount(serviceIndex, 0);
      }
    },
    /**
     * @description Обработать отправку данных шаблона.
     * @returns {void}
     */
    onSubmit() {
      const request = this.makeRequest();
      this.$emit('submit', request);
    },
    /**
     * @description Создать запрос.
     * @returns {WriteOffRequest}
     */
    makeRequest() {
      return new WriteOffRequest(
        this.writeOffId,
        this.products,
        this.currentDate,
      );
    },
    /**
     * @description Удалить товар поступления.
     * @param {number} productIndex Индекс товара.
     * @returns {void}
     */
    removeProduct(productIndex) {
      const exclude = (index) => (product) => product.index !== index;
      const reindex = (acc, product) => acc.cons(
        new Product(
          acc.size(),
          product.id,
          product.name,
          product.count,
          product.unit,
          product.price,
          product.countInWarehouse,
        ),
      );
      this.products = List
        .fromArray(this.products)
        .filter(exclude(productIndex))
        .foldLeft(List())(reindex)
        .reverse()
        .toArray();
    },
    /**
     * @description Получить обновление услуг плана по индексу.
     * @param {function(Product): Product} updater Обновление товара.
     * @returns {function(Number): Product[]}
     */
    updateProductByIndex(updater) {
      return (index) => this.products.map(
        (product) => (product.index === index ? updater(product) : product),
      );
    },
  },
  mixins: [
    formMixin,
    addingServicesFromPriceMixin,
  ],
  name: 'FormWriteoffProducts',
  props: {
    /**
     * @description Дата поступления.
     * @type {Date}
     */
    initialDate: { default: () => new Date(), type: Date, required: true },
    /**
     * @description Идентификатор списания.
     * @type {String|null}
     */
    writeOffId: { default: () => null, type: String },
    /**
     * @description Исходные товары списания.
     * @type {Object[]}
     */
    initialProducts: {
      default: () => [],
      type: Array,
    },
    /**
     * @description Товары.
     * @type {Object[]}
     */
    productOptions: { type: Array, required: true },
    /**
     * @description Ограничения нарушений.
     * @type {any}
     */
    inputErrors: { type: Object, required: true },
  },
};
</script>
<style lang="scss">
@use "@/scss/buttons";
@use "@/scss/fields";
.writeoff-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-block: 20px;
  &__service {
    background-color: var(--section-background-color);
    border: none;
    border-radius: var(--block-border-radius);
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 2rem;
    &-add {
      @include buttons.btn-transparent(12rem, 3rem, 1rem);
      @include buttons.btn-transparent-add(1rem);
      margin: 10px 0;
    }
    &-label {
      @extend %field-label;
    }
    &-remove {
      @include buttons.btn-transparent-remove(1rem);
      margin-left: auto;
    }
    &-title {
      font-weight: 600;
    }
    &__row {
      display: flex;
      flex-direction: row;
      gap: 20px;
      align-items: center;
    }
  }
  &__prices {
    display: flex;
    gap: 1.5rem;
  }
  &__product-price {
    &:hover {
      cursor: pointer;
    }
  }
  &__submit {
    @include buttons.btn-blue(12rem, 3rem, 1rem);
    margin-top: 20px;
    margin-left: auto;
  }
  &__search-input {
    width: 100%;

  }
}
.writeoff-form .writeoff-form__tooth-change,
.writeoff-form .writeoff-form__service-remove {
  min-height: 2rem;
}
.writeoff-form .writeoff-form__tooth-change.empty {
  background: url("@/assets/img/tooth.svg") no-repeat;
  background-size: contain;
  height: 30px;
  width: 1rem;
}
@media screen and (max-width: 480px) {
  .writeoff-form .writeoff-form__actions {
    flex-direction: column;
  }
  .writeoff-form .writeoff-form__service-add {
    @include buttons.btn-transparent(100%, 3rem, 1rem);
  }
  .writeoff-form .writeoff-form__submit {
    @include buttons.btn-blue(100%, 3rem, 1rem);
  }
}
</style>
